import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '../context/LanguageContext';
import '../styles/Suggestions.css';

const bookingUrl = (name) =>
  `https://www.booking.com/searchresults.html?ss=${encodeURIComponent(name + ', Budapest')}`;

const mapsUrl = (name) =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(name + ' Budapest')}`;

const HOTEL_TIERS = [
  {
    tierKey: 'premium',
    priceKey: 'premiumPrice',
    items: [
      { name: 'Budapest Marriott Hotel', descKey: 'marriott' },
      { name: 'Hilton Budapest', descKey: 'hilton' },
    ],
  },
  {
    tierKey: 'upper',
    priceKey: 'upperPrice',
    items: [
      { name: 'Ensana Thermal Margaret Island', descKey: 'ensana' },
      { name: 'Hotel Clark Budapest', descKey: 'clark' },
    ],
  },
  {
    tierKey: 'mid',
    priceKey: 'midPrice',
    items: [
      { name: 'Carlton Hotel Buda Castle', descKey: 'carlton' },
      { name: 'Novotel Budapest Danube', descKey: 'novotel' },
      { name: 'Emerald Hotel Continental', descKey: 'emerald' },
    ],
  },
  {
    tierKey: 'budget',
    priceKey: 'budgetPrice',
    items: [{ name: 'Island Hostel Budapest', descKey: 'island' }],
  },
];

const RESTAURANT_TIERS = [
  {
    tierKey: 'tourist',
    priceKey: 'touristPrice',
    items: [
      {
        name: 'New York Café',
        descKey: 'newYorkCafe',
        tripadvisor: 'https://www.tripadvisor.co.uk/Restaurant_Review-g274887-d970327-Reviews-New_York_Cafe-Budapest_Central_Hungary.html',
      },
      {
        name: 'Párisi Passage',
        descKey: 'parisi',
        tripadvisor: 'https://www.tripadvisor.co.uk/Restaurant_Review-g274887-d19942869-Reviews-Parisi_Passage_Restaurant-Budapest_Central_Hungary.html',
      },
      {
        name: 'Café Gerbeaud',
        descKey: 'gerbeaud',
        tripadvisor: 'https://www.tripadvisor.co.uk/Restaurant_Review-g274887-d952413-Reviews-Gerbeaud-Budapest_Central_Hungary.html',
      },
    ],
  },
  {
    tierKey: 'midRange',
    priceKey: 'midRangePrice',
    items: [
      {
        name: 'QUI Restaurant & Bar',
        descKey: 'qui',
        tripadvisor: 'https://www.tripadvisor.co.uk/Restaurant_Review-g274887-d21180620-Reviews-QUI_Restaurant_Bar-Budapest_Central_Hungary.html',
      },
      {
        name: 'Mazel Tov',
        descKey: 'mazelTov',
        tripadvisor: 'https://www.tripadvisor.co.uk/Restaurant_Review-g274887-d6691676-Reviews-Mazel_Tov-Budapest_Central_Hungary.html',
      },
      {
        name: 'Köleves',
        descKey: 'koleves',
        tripadvisor: 'https://www.tripadvisor.co.uk/Restaurant_Review-g274887-d797853-Reviews-Koleves-Budapest_Central_Hungary.html',
      },
      {
        name: 'Retek Bisztró',
        descKey: 'retek',
        tripadvisor: 'https://www.tripadvisor.co.uk/Restaurant_Review-g274887-d15643967-Reviews-Retek_Bisztro-Budapest_Central_Hungary.html',
      },
      {
        name: 'Kiskakukk',
        descKey: 'kiskakukk',
        tripadvisor: 'https://www.tripadvisor.co.uk/Restaurant_Review-g274887-d782615-Reviews-Kiskakukk_Etterem-Budapest_Central_Hungary.html',
      },
      {
        name: 'Mandragóra',
        descKey: 'mandragora',
        tripadvisor: 'https://www.tripadvisor.co.uk/Restaurant_Review-g274887-d712503-Reviews-Mandragora-Budapest_Central_Hungary.html',
      },
      {
        name: 'Komachi Bistro',
        descKey: 'komachi',
        tripadvisor: 'https://www.tripadvisor.co.uk/Restaurant_Review-g274887-d6672991-Reviews-Komachi_Bistro-Budapest_Central_Hungary.html',
      },
      {
        name: 'Indigo',
        descKey: 'indigo',
        tripadvisor: 'https://www.tripadvisor.co.uk/Restaurant_Review-g274887-d1145116-Reviews-Indigo-Budapest_Central_Hungary.html',
      },
    ],
  },
  {
    tierKey: 'casual',
    priceKey: 'casualPrice',
    items: [
      {
        name: 'Szeráj Török Étterem',
        descKey: 'szeraj',
        tripadvisor: 'https://www.tripadvisor.co.uk/Restaurant_Review-g274887-d1010027-Reviews-Szeraj_Torok_Etterem-Budapest_Central_Hungary.html',
      },
      {
        name: 'Heuréka Original Greek Food',
        descKey: 'heureka',
        tripadvisor: 'https://www.tripadvisor.co.uk/Restaurant_Review-g274887-d26442680-Reviews-Heureka_Original_Greek_Food-Budapest_Central_Hungary.html',
      },
    ],
  },
];

const THINGS_TO_DO = [
  { groupKey: 'baths', items: [
    { name: 'Rudas Baths', descKey: 'rudas', url: 'https://www.rudasfurdo.hu/' },
    { name: 'Széchenyi Thermal Baths', descKey: 'szechenyi', url: 'https://www.szechenyifurdo.hu/' },
    { name: 'Gellért Baths', descKey: 'gellert', url: 'https://www.gellertfurdo.hu/' },
    { name: 'Lukács Baths', descKey: 'lukacs', url: 'https://www.lukacsfurdo.hu/' },
  ]},
  { groupKey: 'nightlife', items: [
    { name: 'Szimpla Kert', descKey: 'szimpla', url: 'https://www.tripadvisor.co.uk/Attraction_Review-g274887-d668397-Reviews-Szimpla_Kert-Budapest_Central_Hungary.html' },
    { name: 'Fekete Kutya', descKey: 'feketeKutya', url: 'https://www.tripadvisor.co.uk/Restaurant_Review-g274887-d9779374-Reviews-Fekete_Kutya-Budapest_Central_Hungary.html' },
    { name: 'Gozsdu Udvar', descKey: 'gozsdu', url: 'https://www.google.com/maps/search/?api=1&query=Gozsdu+Udvar+Budapest' },
    { name: 'Budapest Park', descKey: 'budapestPark', url: 'https://www.budapestpark.hu/' },
    { name: 'Jardin Cocktail Bar', descKey: 'jardin', url: 'https://www.tripadvisor.co.uk/Restaurant_Review-g274887-d12430700-Reviews-Jardin_Cocktail_Bar-Budapest_Central_Hungary.html' },
    { name: 'Pontoon', descKey: 'pontoon', url: 'https://www.tripadvisor.com/Attraction_Review-g274887-d14013218-Reviews-Pontoon_Budapest-Budapest_Central_Hungary.html' },
  ]},
  { groupKey: 'sights', items: [
    { name: 'Buda Castle & Fisherman’s Bastion', descKey: 'castle', url: 'https://www.google.com/maps/search/?api=1&query=Fisherman%27s+Bastion+Budapest' },
    { name: 'Hungarian Parliament', descKey: 'parliament', url: 'https://latogatokozpont.parlament.hu/en/' },
    { name: 'Heroes’ Square & City Park', descKey: 'heroes', url: 'https://www.google.com/maps/search/?api=1&query=Heroes+Square+Budapest' },
    { name: 'Margaret Island', descKey: 'margaret', url: 'https://www.google.com/maps/search/?api=1&query=Margaret+Island+Budapest' },
    { name: 'Chain Bridge & Danube Promenade', descKey: 'chainBridge', url: 'https://www.google.com/maps/search/?api=1&query=Chain+Bridge+Budapest' },
    { name: 'Great Market Hall', descKey: 'marketHall', url: 'https://www.google.com/maps/search/?api=1&query=Great+Market+Hall+Budapest' },
    { name: 'Citadella & Liberty Statue', descKey: 'citadella', url: 'https://www.google.com/maps/search/?api=1&query=Citadella+Budapest' },
    { name: 'Danube River Cruise', descKey: 'cruise', url: 'https://www.google.com/maps/search/?api=1&query=Danube+River+Cruise+Budapest' },
  ]},
];

const SimpleCard = ({ item, t, descNamespace, delay, ctaKey }) => (
  <motion.a
    href={item.url || item.bookingUrl}
    target="_blank"
    rel="noopener noreferrer"
    className="suggestion-card"
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-50px' }}
    transition={{ duration: 0.4, delay }}
    whileHover={{ y: -4 }}
  >
    <div className="suggestion-card-accent" />
    <h4 className="suggestion-name">{item.name}</h4>
    <p className="suggestion-desc">{t(`suggestions.${descNamespace}.${item.descKey}`)}</p>
    {ctaKey && <span className="suggestion-cta">{t(ctaKey)} →</span>}
  </motion.a>
);

const RestaurantCard = ({ item, t, delay }) => (
  <motion.div
    className="suggestion-card suggestion-card-actions"
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-50px' }}
    transition={{ duration: 0.4, delay }}
    whileHover={{ y: -4 }}
  >
    <div className="suggestion-card-accent" />
    <h4 className="suggestion-name">{item.name}</h4>
    <p className="suggestion-desc">{t(`suggestions.restaurants.${item.descKey}`)}</p>
    <div className="suggestion-actions">
      <a
        href={item.tripadvisor}
        target="_blank"
        rel="noopener noreferrer"
        className="suggestion-action"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
          <polyline points="15 3 21 3 21 9" />
          <line x1="10" y1="14" x2="21" y2="3" />
        </svg>
        {t('suggestions.actions.tripadvisor')}
      </a>
      <a
        href={mapsUrl(item.name)}
        target="_blank"
        rel="noopener noreferrer"
        className="suggestion-action"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
        {t('suggestions.actions.maps')}
      </a>
    </div>
  </motion.div>
);

const Suggestions = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('stay');

  const tabs = [
    { key: 'stay', labelKey: 'suggestions.tabs.stay' },
    { key: 'eat', labelKey: 'suggestions.tabs.eat' },
    { key: 'do', labelKey: 'suggestions.tabs.do' },
  ];

  return (
    <section id="suggestions" className="suggestions">
      <div className="suggestions-container">
        <motion.div
          className="suggestions-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="suggestions-title">{t('suggestions.title')}</h2>
          <p className="suggestions-subtitle">{t('suggestions.subtitle')}</p>
        </motion.div>

        <motion.div
          className="suggestions-tabs"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          {tabs.map((tab) => (
            <motion.button
              key={tab.key}
              className={`suggestions-tab ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.key)}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
            >
              {t(tab.labelKey)}
            </motion.button>
          ))}
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.3 }}
            className="suggestions-content"
          >
            {activeTab === 'stay' && (
              <div className="suggestions-tiers">
                {HOTEL_TIERS.map((tier) => (
                  <div key={tier.tierKey} className="suggestions-tier">
                    <div className="tier-header">
                      <h3 className="tier-name">{t(`suggestions.tiers.${tier.tierKey}`)}</h3>
                      <span className="tier-price">{t(`suggestions.tiers.${tier.priceKey}`)}</span>
                    </div>
                    <div className="suggestions-grid">
                      {tier.items.map((item, i) => (
                        <SimpleCard
                          key={item.name}
                          item={{ ...item, url: bookingUrl(item.name) }}
                          t={t}
                          descNamespace="hotels"
                          delay={i * 0.05}
                          ctaKey="suggestions.actions.booking"
                        />
                      ))}
                    </div>
                  </div>
                ))}
                <p className="suggestions-note">{t('suggestions.stayNote')}</p>
              </div>
            )}

            {activeTab === 'eat' && (
              <div className="suggestions-tiers">
                {RESTAURANT_TIERS.map((tier) => (
                  <div key={tier.tierKey} className="suggestions-tier">
                    <div className="tier-header">
                      <h3 className="tier-name">{t(`suggestions.restaurantTiers.${tier.tierKey}`)}</h3>
                      <span className="tier-price">{t(`suggestions.restaurantTiers.${tier.priceKey}`)}</span>
                    </div>
                    <div className="suggestions-grid">
                      {tier.items.map((item, i) => (
                        <RestaurantCard key={item.name} item={item} t={t} delay={i * 0.04} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'do' && (
              <div className="suggestions-groups">
                {THINGS_TO_DO.map((group) => (
                  <div key={group.groupKey} className="suggestions-tier">
                    <div className="tier-header">
                      <h3 className="tier-name">{t(`suggestions.groups.${group.groupKey}`)}</h3>
                    </div>
                    <div className="suggestions-grid">
                      {group.items.map((item, i) => (
                        <SimpleCard
                          key={item.name}
                          item={item}
                          t={t}
                          descNamespace="todo"
                          delay={i * 0.04}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Suggestions;
